/* eslint-disable @typescript-eslint/no-empty-function */
import React, { useEffect, useState } from 'react';
import Beneficiaries from 'domain/Beneficiaries/Beneficiaries';
import { components } from 'generated/payment-initialisation/schema';
import useAlphaSnackbar from 'hooks/useAlphaSnackbar';
import useSwitchAccount from 'hooks/useSwitchAccount';
import { TCountries } from 'models/countries';
import routes from 'routes.path';
import BeneficiariesService from 'services/Beneficiaries/beneficiaries.service';
import RemittersService from 'services/Remitters/remitters.service';
import t from 'utils/translationHelper';

import { CurrencyList } from '@alpha/bene-dtos/lib/responses/CurrencyList';
import { CurrencyAccountDto } from '@alpha/currency-accounts-dtos';
import { Alert } from '@alpha/ui-lib/ui/Alert';
import { APMainLayout } from '@alpha/ui-lib/ui/MainLayout';
import { StyledTabsWrapper } from '@alpha/ui-lib/ui/Tabs';

import Remitters from './Remitters/Remitters';
import MigrationOverview from './MigrationOverview/MigrationOverview';
import { faCheckCircle, faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import ConfigService from 'services/Config/config.service';
import mapMigrationReadinessStatusToDisplay from 'utils/mapMigrationReadinessStatusToDisplay';
import { Status } from '@alpha/ui-lib/ui/Status';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import StatementsService from 'services/Statements/statements.service';

const MigrationReadiness: React.FC = () => {
  const [remitters, setRemitters] = useState<components['schemas']['RemittersListResponse']>([]);
  const { currentAccount } = useSwitchAccount();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('actionRequired');
  const [searchQuery, setSearchQuery] = useState('');
  const [pageNumber, setPageNumber] = useState(0);
  const [countries, setCountries] = useState<TCountries>([]);
  const [currencies, setCurrencies] = useState<CurrencyList>({ currencies: [], topCurrencies: [] });
  const [tabIndex, setTabIndex] = useState(0);
  const [migrationReadiness, setMigrationReadiness] = useState<components['schemas']['MigrationReadinessResponse']>(null);
  const [currencyAccounts, setCurrencyAccounts] = useState<CurrencyAccountDto[]>([]);

  const sb = useAlphaSnackbar();
  const accountInfo = JSON.parse(localStorage.getItem('account-info') || '{}');
  const isMigrationCenter = currentAccount.dataCenterVersion === 'Corpay Migration Center';

  const getMigrationReadiness = async () => {
    const migrationReadinessResponse = await ConfigService.getMigrationReadiness();
    return migrationReadinessResponse;
  };

  useEffect(() => {
    const fetchMigrationReadiness = async () => {
      const response = await getMigrationReadiness();
      setMigrationReadiness(response);
    };
    fetchMigrationReadiness();
  }, []);

  useEffect(() => {
    const fetchCurrencyAccounts = async () => {
      try {
        const currencyAccountsResponse = await StatementsService.getStatementAccounts();
        const currencyAccounts = currencyAccountsResponse.currencyAccounts;
        setCurrencyAccounts(currencyAccounts);
      } catch (error) {
        console.error('Error fetching currency accounts', error);
      }
    };
    if (isMigrationCenter) {
      fetchCurrencyAccounts();
    }
  }, []);

  const getRemitters = async (signal?: AbortSignal) => {
    setIsLoading(true);
    try {
      const remittersResponse = await RemittersService.getRemitters(
        currentAccount?.id, activeTab !== 'allRemitters',
        searchQuery,
        pageNumber,
        signal,
      );
      // Only update state if this request wasn't aborted
      if (!signal?.aborted) {
        setRemitters(remittersResponse.data);
      }
    } catch (error: unknown) {
      // Ignore abort errors
      const isAbortError = error instanceof Error
        && (error.name === 'AbortError' || error.name === 'CanceledError');
      if (isAbortError) {
        return;
      }
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      sb.trigger(errorMessage, 'error');
      console.error(error);
    } finally {
      if (!signal?.aborted) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    const abortController = new AbortController();
    getRemitters(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [activeTab, pageNumber, searchQuery]);

  async function getCountries() {
    try {
      setCountries(await BeneficiariesService.getCountries());
    } catch (e) {
      sb.trigger(e.response?.data?.error || t('there_was_an_issue_retrieving_countries'));
      console.error({ action: 'Error getting beneficiary countries.' });
    }
  }

  async function getCurrencies() {
    try {
      setCurrencies(await BeneficiariesService.getCurrencies());
    } catch (e) {
      sb.trigger(e.response?.data?.error || t('there_was_an_issue_retrieving_currencies'));
      console.error({ action: 'Error getting beneficiary currencies.' });
    }
  }

  useEffect(() => {
    getCountries();
    getCurrencies();
  }, []);

  useEffect(() => {
    if (tabIndex === 0) {
      setActiveTab('actionRequired');
    }
  }, [tabIndex]);

  return (
    <>
      <APMainLayout
        sticky
        height="165px"
        data-id="migration_center"
        title={(
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <div style={{ marginRight: '8px' }}>{isMigrationCenter ? t('migration_readiness') : t('missing_data')}</div>
            {migrationReadiness?.TotalReadinessPercentage === 100 && <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '4px' }} />}
            <Status variant={mapMigrationReadinessStatusToDisplay(migrationReadiness?.TotalReadinessPercentage ?? 0).variant}>{mapMigrationReadinessStatusToDisplay(migrationReadiness?.TotalReadinessPercentage ?? 0).text}</Status>
          </div>
)}
        breadCrumbs={[{ text: t('home'), url: routes.dashboard }]}
        pageTabs={(
          <StyledTabsWrapper
            testId="migration-readiness-tabs"
            tabTitles={isMigrationCenter ? [t('migration_overview'), t('beneficiaries'), t('remitters')] : [t('beneficiaries'), t('remitters')]}
            tabIndex={tabIndex}
            handleChange={setTabIndex}
          />
        )}
      >
        {isMigrationCenter && (
        <div style={{ marginTop: '16px' }}>
          {tabIndex === 1 && <Alert variant="info" text={t('migration_center_alert')} />}
          {tabIndex === 2 && <Alert variant="error" text={t('remitters_alert')} overrideIcon={faInfoCircle} />}
        </div>
        )}
        {tabIndex === 0 && isMigrationCenter && (
          <MigrationOverview migrationReadiness={migrationReadiness} accountInfo={accountInfo} onNavigateToTab={setTabIndex} currencyAccounts={currencyAccounts} />
        )}
        {((tabIndex === 1 && isMigrationCenter) || (!isMigrationCenter && tabIndex === 0)) && (
          <Beneficiaries isMigrationReadiness />
        )}
        {((tabIndex === 2 && isMigrationCenter) || (!isMigrationCenter && tabIndex === 1)) && (
        <Remitters
          remitters={remitters}
          refetchRemitters={getRemitters}
          isLoading={isLoading}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          setSearchQuery={setSearchQuery}
          searchQuery={searchQuery}
          setPageNumber={setPageNumber}
          pageNumber={pageNumber}
          countries={countries}
          currencies={currencies}
        />
        )}
      </APMainLayout>
    </>
  );
};

export default MigrationReadiness;
